'use client';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import React from 'react';
import {
   Box,
   Flex,
   Table,
   Tbody,
   Td,
   Text,
   Tr,
   useColorModeValue,
   useDisclosure,
   Checkbox,
   Drawer,
   DrawerOverlay,
   DrawerContent,
   DrawerCloseButton,
   DrawerHeader,
   DrawerBody,
   useOutsideClick,
   Spinner,
   Thead,
   Th,
   Grid,
   GridItem,
   Input,
   Button,
   Modal,
   ModalOverlay,
   ModalContent,
   ModalHeader,
   ModalFooter,
   ModalBody,
   ModalCloseButton,
   Image,
   IconButton,
   Tooltip,
   Popover,
   PopoverTrigger,
   PopoverContent,
   PopoverBody,
   useColorMode,
} from '@chakra-ui/react';
import Select from 'react-select';
import { LuRefreshCcw } from 'react-icons/lu';
import { motion } from 'framer-motion';
import { SearchIcon, ViewIcon, ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import SalesDetails from 'components/salesDetails/SalesDetails';
import { deleteSaleInvoice, getSingleWithPrevAndNext } from '../../../../redux/actions/salesInvoice';
import SalesPurchaseFilter from '../components/SalesPurchaseFilter';
import { deleteRecord } from 'utils/util';
import {
   createColumnHelper,
   flexRender,
   getCoreRowModel,
   getSortedRowModel,
   useReactTable,
} from '@tanstack/react-table';

// Assets
import nodata from 'assets/img/nfts/no data.svg';
import Download from '../../../../assets/img/nfts/download.svg';
import xls from '../../../../../src/assets/img/nfts/xls.svg';
import back from '../../../../../src/assets/img/nfts/back.svg';
import cancel from '../../../../assets/img/nfts/cancel.svg';
import DotIcon from '../../../../assets/img/nfts/dot.svg';

// Components
import Card from 'components/card/Card';
import PeriodDatePicker from 'components/RangePicker/PeriodDatePicker';
import { renderDynamicDropdown } from 'components/ui/DynamicDropdown';

// Redux
import { useDispatch, useSelector } from 'react-redux';
import { getReportSaleDetail } from '../../../../redux/actions/reports';

// Utils
import { getShareCompanyOptions } from 'utils/util';
import { columnsReportsSalesDetail } from '../../customDataTable/variables/columnsData';
import { REPORT_TYPE } from 'constants';

import * as XLSX from 'xlsx-js-style';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import autoTable from 'jspdf-autotable';
import { useGlobal } from 'contexts';
import { IoFilterOutline } from 'react-icons/io5';
import { getSingleSalesReturnWithPrevAndNext } from '../../../../redux/actions/salesReturnInvoice';
import { useUser } from 'contexts';
import Loader from 'views/admin/default/Loader';

const MotionFlex = motion(Flex);
const MotionSearchIcon = motion(SearchIcon);

// Constants
const reportTypeOptions = [
   { value: 'date', label: 'Date' },
   { value: 'party', label: 'Party' },
   { value: 'broker', label: 'Broker' },
];

const SalesReturnSummary = () => {
   // Refs and state management
   const abortControllerRef = useRef(null);
   const searchTimeoutRef = useRef(null);
   const wrapperRef = useRef();
   const fromDateInputRef = useRef(null);
   const requestCacheRef = useRef({});
   const isMountedRef = useRef(true);

   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';

   // Theme colors
   const textColor = useColorModeValue('secondaryGray.900', 'white');
   const backgroundColor = useColorModeValue('white', '#101F4A');
   const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
   const bg = useColorModeValue('#f2f7f2', '#1a254b');
   const icon = useColorModeValue('#000000', '#ffffff');
   const controlBg = useColorModeValue('#FFFFFF', '#2D3748');
   const controlBorder = useColorModeValue('#E2E8F0', '#4A5568');
   const controlBorderHover = useColorModeValue('#CBD5E0', '#718096');
   const focusBoxShadow = useColorModeValue('0 0 0 1px #3182CE', '0 0 0 1px #63B3ED');
   const placeholderColor = useColorModeValue('#A0AEC0', '#718096');
   const optionBg = useColorModeValue('#FFFFFF', '#2D3748');
   const optionHoverBg = useColorModeValue('#EDF2F7', '#4A5568');
   const menuBg = useColorModeValue('#FFFFFF', '#2D3748');
   const menuBorder = useColorModeValue('#E2E8F0', '#4A5568');
   const indicatorColor = useColorModeValue('#718096', '#A0AEC0');
   const indicatorHoverColor = useColorModeValue('#4A5568', '#CBD5E0');

   // State
   const [companyFilter, setCompanyFilter] = useState([]);
   const [reportType, setReportType] = useState('date');
   const [expandedItems, setExpandedItems] = useState({});
   const [searchTerm, setSearchTerm] = useState('');
   const [sorting, setSorting] = useState([]);
   const [columnVisibility, setColumnVisibility] = useState({});
   const [isColumnSelectorOpen, setIsColumnSelectorOpen] = useState(false);
   const [isLoading, setIsLoading] = useState(false);
   const [expanded, setExpanded] = useState(false);
   const [isModalLoading, setIsModalLoading] = useState(false);
   const [isFilterLoading, setIsFilterLoading] = useState(false);
   const isFirstRenderFilter = useRef(true);

   const { isOpen: isFilterOpen, onOpen: onFilterOpen, onClose: onFilterClose } = useDisclosure();
   const companyIds = useMemo(
      () =>
         companyFilter.length > 0
            ? companyFilter
            : localStorage.getItem('companyId')
               ? [parseInt(localStorage.getItem('companyId'))]
               : [],
      [companyFilter],
   );

   // Redux
   const dispatch = useDispatch();
   const company = useSelector((state) => state.company.list || []);
   const salesReport = useSelector((state) => state.reports?.list || []);
   const { isOpen, onOpen, onClose } = useDisclosure();
   const [dateRange, setDateRange] = useState(() => {
      const today = new Date();
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(today.getDate() - 6);

      return {
         fromDate: sevenDaysAgo.toISOString().split('T')[0], // last 7th day
         toDate: today.toISOString().split('T')[0], // today
      };
   });
   const viewData = useSelector((state) => state.salesReturnInvoice?.saleReturnData);
   const { viewDetail, paginate } = useMemo(() => {
      const data = viewData?.data[0];

      const pagination = viewData?.pagination;

      return { viewDetail: data, paginate: pagination };
   }, [viewData?.data, viewData?.pagination]);
   const [isEditFormOpen, setIsEditFormOpen] = React.useState(false);
   const [isOpenPurchaseReturnSingleView, setIsOpenPurchaseReturnSingleView] = React.useState(null);
   const { user } = useUser();
   const admin = user?.isAdmin;
   const rights = user?.userRights?.find((ur) => ur?.companyId === user?.loginhistory?.companyId);
   const companyId = Number(localStorage.getItem('companyId'));
   const userRights = user?.user?.userRights || user?.userRights;
   const saleInvoiceRight = Array.isArray(userRights)
      ? userRights.find((right) => right.companyId === companyId)?.saleinvoice
      : null;
   const saleReturnRight = Array.isArray(userRights)
      ? userRights.find((right) => right.companyId === companyId)?.salereturn
      : null;

   // Constants
   const dbName = company?.dbName;
   const columnHelper = createColumnHelper();
   const { companyList } = useGlobal();
   const { userGlobalSettingsData } = useGlobal();

   // Memoized values
   const companyOptions = useMemo(() => {
      return getShareCompanyOptions({ dbName, company: companyList });
   }, [companyList, dbName]);

   const lastcompanyOptions = useMemo(() => {
      const currentUserCode = user?.loginhistory?.code;
      const currentCompanyId = user?.loginhistory?.companyId;

      // SHARED COMPANY MODE: When code is present
      if (currentUserCode) {
         // Find the company with matching ID AND matching dbName
         const sharedCompany = companyList.find(
            company => company.id === currentCompanyId && company.dbName === currentUserCode
         );

         if (sharedCompany) {
            return [{
               value: sharedCompany.id,
               label: sharedCompany.name,
               id: sharedCompany.id,
               name: sharedCompany.name,
            }];
         }

         // Fallback: try to find by dbName only
         const companiesWithDbName = companyList.filter(company => company.dbName === currentUserCode);
         if (companiesWithDbName.length > 0) {
            return companiesWithDbName.map(company => ({
               value: company.id,
               label: company.name,
               id: company.id,
               name: company.name,
            }));
         }

         return [];
      }

      // NORMAL MODE: When no code, show companies without dbName
      return companyList
         .filter(company => !company.dbName)
         .map(company => ({
            value: company.id,
            label: company.name,
            id: company.id,
            name: company.name,
         }));
   }, [companyList, user?.loginhistory?.code, user?.loginhistory?.companyId]);

   const columns = useMemo(() => {
      return columnsReportsSalesDetail.map((column) =>
         columnHelper.accessor(column.id, {
            id: column.id,
            header: () => (
               <Text
                  justifyContent="space-between"
                  align="center"
                  fontSize={{ sm: '10px', lg: '12px' }}
                  color={textColor}>
                  {column.header}
               </Text>
            ),
            cell: (info) => <Flex align="center">{info.getValue()}</Flex>,
         }),
      );
   }, [columnHelper, textColor]);

   // Table instance
   const table = useReactTable({
      data: salesReport,
      columns,
      state: { sorting, columnVisibility },
      onSortingChange: setSorting,
      onColumnVisibilityChange: setColumnVisibility,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
   });

   useEffect(() => {
      const initialColumnVisibility = {};
      columnsReportsSalesDetail.forEach((column) => {
         initialColumnVisibility[column.id] = true;
      });
      setColumnVisibility(initialColumnVisibility);
   }, []);

   useEffect(() => {
      const storedCompanyId = localStorage.getItem('companyId');
      if (storedCompanyId && companyList.length > 0) {
         const companyId = parseInt(storedCompanyId);
         const matchingCompany = companyList.find((comp) => comp.id === companyId);
         if (matchingCompany && !companyFilter.includes(matchingCompany.id)) {
            setCompanyFilter([matchingCompany.id]);
         }
      }
   }, [companyList]); // Remove companyFilter from dependencies

   useEffect(() => {
      if (salesReport && salesReport.length > 0 && reportType === REPORT_TYPE.party) {
         const initialExpandedState = {};
         salesReport.forEach((companyData) => {
            initialExpandedState[`company-${companyData.companyId}`] = true;
            if (companyData.data && Array.isArray(companyData.data)) {
               companyData.data.forEach((accountData) => {
                  initialExpandedState[`account-${accountData.accountId}`] = true;
               });
            }
         });
         setExpandedItems(initialExpandedState);
      }
   }, [salesReport, reportType]);

   useEffect(() => {
      if (isFirstRenderFilter.current) {
         isFirstRenderFilter.current = false;
         return;
      }
      setIsFilterLoading(false);
   }, [salesReport])

   const handleCompanyChange = useCallback((selectedOptions) => {
      setCompanyFilter(selectedOptions ? selectedOptions : []);
   }, []);

   const handleDateRangeChange = useCallback((dates) => {
      if (!dates || dates.length !== 2) return;
      const [from, to] = dates.map((date) => (typeof date === 'string' ? date : date.toISOString().slice(0, 10)));
      setDateRange({ fromDate: from, toDate: to });
   }, []);

   const toggleExpand = useCallback((id) => {
      setExpandedItems((prev) => ({
         ...prev,
         [id]: !prev[id],
      }));
   }, []);

   const fetchData = useCallback(
      async (searchTerm = '') => {
         // Cancel previous request
         if (abortControllerRef.current) {
            abortControllerRef.current.abort();
         }

         abortControllerRef.current = new AbortController();
         setIsLoading(true);

         try {
            const companyIds =
               companyFilter.length > 0
                  ? companyFilter
                  : localStorage.getItem('companyId')
                     ? [parseInt(localStorage.getItem('companyId'))]
                     : [];

            // Only fetch if we have valid parameters
            if (dateRange.fromDate && dateRange.toDate && companyIds.length > 0) {
               // Create cache key
               const cacheKey = `${companyIds.toString()}-${reportType}-${dateRange.fromDate}-${dateRange.toDate
                  }-${searchTerm}`;

               // Check if we have a recent cached response (within 5 seconds)
               const cachedResponse = requestCacheRef.current[cacheKey];
               const now = Date.now();

               if (cachedResponse && now - cachedResponse.timestamp < 5000) {
                  // Use cached data if available and recent
                  setIsLoading(false);
                  return;
               }

               await dispatch(
                  getReportSaleDetail({
                     companyIds: companyIds.toString(),
                     type: reportType,
                     isReturn: 1,
                     fromDate: dateRange.fromDate,
                     toDate: dateRange.toDate,
                     search: searchTerm.trim(),
                  }),
                  { signal: abortControllerRef.current.signal },
               );

               // Cache the response
               requestCacheRef.current[cacheKey] = {
                  timestamp: Date.now(),
               };
            }
         } catch (error) {
            if (error.name !== 'AbortError') {
               console.error('Error fetching sales details:', error);
            }
         } finally {
            if (isMountedRef.current) {
               setIsLoading(false);
            }
            abortControllerRef.current = null;
         }
      },
      [dispatch, companyFilter, reportType, dateRange.fromDate, dateRange.toDate], // More specific dependencies
   );

   useEffect(() => {
      isMountedRef.current = true;

      const timer = setTimeout(() => {
         if (isMountedRef.current) {
            fetchData();
         }
      }, 300);

      return () => {
         clearTimeout(timer);
         isMountedRef.current = false;
         if (abortControllerRef.current) {
            abortControllerRef.current.abort();
         }
      };
   }, [reportType, dateRange.fromDate, dateRange.toDate, fetchData]);
   useEffect(() => {
      return () => {
         isMountedRef.current = false;
         if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
         }
         if (abortControllerRef.current) {
            abortControllerRef.current.abort();
         }
      };
   }, []);

   const handleSearchChange = useCallback(
      (e) => {
         const value = e.target.value;
         setSearchTerm(value);

         if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
         }

         searchTimeoutRef.current = setTimeout(() => {
            fetchData(value);
         }, 500); // Increased debounce time
      },
      [fetchData],
   );

   const handleRefresh = useCallback(() => {
      // Clear cache for current parameters to force refresh
      const companyIds =
         companyFilter.length > 0
            ? companyFilter
            : localStorage.getItem('companyId')
               ? [parseInt(localStorage.getItem('companyId'))]
               : [];

      const cacheKey = `${companyIds.toString()}-${reportType}-${dateRange.fromDate}-${dateRange.toDate}-${searchTerm}`;
      delete requestCacheRef.current[cacheKey];

      fetchData(searchTerm);
   }, [fetchData, companyFilter, reportType, dateRange, searchTerm]);

   const handleNextPage = () => {
      console.log('Next');
   };
   const handlePrevPage = () => {
      console.log('Prev');
   };

   // Cleanup effects
   useEffect(() => {
      return () => {
         if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
         }
         if (abortControllerRef.current) {
            abortControllerRef.current.abort();
         }
      };
   }, []);

   useOutsideClick({
      ref: wrapperRef,
      handler: () => {
         if (searchTerm === '') setExpanded(false);
      },
   });

   const handleDownloadExcel = useCallback(() => {
      if (!salesReport || salesReport.length === 0) return;

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([]);

      // Formatting helpers
      const formatNumber = (value) => {
         const num = Number(value);
         return isNaN(num) ? '0' : num.toLocaleString('en-IN');
      };

      const formatCurrency = (value) => {
         const num = Number(value || 0);
         return `₹${num.toLocaleString('en-IN')}`;
      };

      const formatDate = (dateString) => {
         if (!dateString) return '';
         const date = new Date(dateString);
         return date.toLocaleDateString('en-IN');
      };

      // Add title and date range
      const title = 'Sales Summary Report';
      const dateRange =
         dateRange.fromDate && dateRange.toDate
            ? `From ${formatDate(dateRange.fromDate)} to ${formatDate(dateRange.toDate)}`
            : 'All Dates';

      XLSX.utils.sheet_add_aoa(ws, [[title]], { origin: 'A1' });
      XLSX.utils.sheet_add_aoa(ws, [[dateRange]], { origin: 'A2' });

      // Style title and date range
      ws['A1'].s = {
         font: { sz: 16, bold: true, color: { rgb: '000000' } },
         alignment: { horizontal: 'left' },
      };
      ws['A2'].s = {
         font: { sz: 12, color: { rgb: '000000' } },
         alignment: { horizontal: 'left' },
      };

      // Get visible columns
      const visibleColumns = columnsReportsSalesDetail.filter((col) => columnVisibility[col.id] !== false);
      const columnIds = visibleColumns.map((col) => col.id);

      // Start data from row 4
      let rowIndex = 3;

      if (reportType === REPORT_TYPE.party) {
         // Party-wise report format
         salesReport.forEach((companyData) => {
            // Company header
            XLSX.utils.sheet_add_aoa(ws, [[companyData.companyName]], {
               origin: XLSX.utils.encode_cell({ r: rowIndex, c: 0 }),
            });
            ws[XLSX.utils.encode_cell({ r: rowIndex, c: 0 })].s = {
               font: { sz: 14, bold: true, color: { rgb: '000000' } },
               fill: { fgColor: { rgb: 'F2F7F2' } },
            };

            // Merge company header across all columns
            ws['!merges'] = [
               ...(ws['!merges'] || []),
               XLSX.utils.decode_range(
                  `${XLSX.utils.encode_cell({ r: rowIndex, c: 0 })}:${XLSX.utils.encode_cell({
                     r: rowIndex,
                     c: visibleColumns.length - 1,
                  })}`,
               ),
            ];
            rowIndex++;

            // Calculate company totals
            const companyTotals = companyData.data.reduce(
               (acc, account) => {
                  acc.invoices += account.data?.length || 0;
                  acc.qty1 += Number(account.totalQty1) || 0;
                  acc.qty2 += Number(account.totalQty2) || 0;
                  acc.qty3 += Number(account.totalQty3) || 0;
                  acc.netAmount += Number(account.totalNetAmt) || 0;
                  return acc;
               },
               { invoices: 0, qty1: 0, qty2: 0, qty3: 0, netAmount: 0 },
            );

            // Add company summary
            const summaryRow = [
               'Company Summary',
               `Invoices: ${companyTotals.invoices}`,
               `Qty 1: ${formatNumber(companyTotals.qty1)}`,
               `Qty 2: ${formatNumber(companyTotals.qty2)}`,
               `Qty 3: ${formatNumber(companyTotals.qty3)}`,
               `Net Amount: ${formatCurrency(companyTotals.netAmount)}`,
            ];
            XLSX.utils.sheet_add_aoa(ws, [summaryRow], { origin: XLSX.utils.encode_cell({ r: rowIndex, c: 0 }) });

            // Style summary row
            summaryRow.forEach((_, colIndex) => {
               ws[XLSX.utils.encode_cell({ r: rowIndex, c: colIndex })].s = {
                  font: { bold: true, color: { rgb: '000000' } },
               };
            });
            rowIndex++;

            // Add account data
            companyData.data.forEach((accountData) => {
               // Account summary row
               const accountRow = [
                  accountData.accountName,
                  accountData.data?.length || 0,
                  formatNumber(accountData.totalQty1),
                  formatNumber(accountData.totalQty2),
                  formatNumber(accountData.totalQty3),
                  formatCurrency(accountData.totalNetAmt),
               ];
               XLSX.utils.sheet_add_aoa(ws, [accountRow], { origin: XLSX.utils.encode_cell({ r: rowIndex, c: 0 }) });

               // Style account row
               accountRow.forEach((_, colIndex) => {
                  ws[XLSX.utils.encode_cell({ r: rowIndex, c: colIndex })].s = {
                     font: { color: { rgb: '000000' } },
                     fill: { fgColor: { rgb: colIndex === 0 ? 'E6E6E6' : 'F5F5F5' } },
                     alignment: { horizontal: colIndex === 0 ? 'left' : 'right' },
                  };
               });
               rowIndex++;
            });

            // Add space between companies
            rowIndex += 2;
         });
      } else {
         // Date-wise report format
         const headers = visibleColumns.map((col) => col.header);
         XLSX.utils.sheet_add_aoa(ws, [headers], { origin: XLSX.utils.encode_cell({ r: rowIndex, c: 0 }) });

         // Style headers
         headers.forEach((_, colIndex) => {
            ws[XLSX.utils.encode_cell({ r: rowIndex, c: colIndex })].s = {
               font: { bold: true, color: { rgb: 'FFFFFF' } },
               fill: { fgColor: { rgb: '4F81BD' } },
               alignment: { horizontal: 'center' },
            };
         });
         rowIndex++;

         // Add transaction data
         salesReport.forEach((dateItem) => {
            // Date header
            XLSX.utils.sheet_add_aoa(ws, [[dateItem.companyName]], {
               origin: XLSX.utils.encode_cell({ r: rowIndex, c: 0 }),
            });
            ws[XLSX.utils.encode_cell({ r: rowIndex, c: 0 })].s = {
               font: { sz: 14, bold: true, color: { rgb: '000000' } },
               fill: { fgColor: { rgb: 'F2F7F2' } },
            };

            // Merge date header across all columns
            ws['!merges'] = [
               ...(ws['!merges'] || []),
               XLSX.utils.decode_range(
                  `${XLSX.utils.encode_cell({ r: rowIndex, c: 0 })}:${XLSX.utils.encode_cell({
                     r: rowIndex,
                     c: visibleColumns.length - 1,
                  })}`,
               ),
            ];
            rowIndex++;

            // Add transactions for this date
            dateItem.data?.forEach((item) => {
               item.data?.forEach((transaction) => {
                  const transactionRow = columnIds.map((colId) => {
                     const value = transaction[colId];
                     if (['netAmount', 'basicAmount', 'taxableAmount', 'gstAmount'].includes(colId)) {
                        return formatCurrency(value);
                     } else if (colId === 'date') {
                        return formatDate(value);
                     } else if (colId === 'itemName' && transaction.saleLineItems) {
                        return transaction.saleLineItems.map((item) => item.itemName).join(', ');
                     }
                     return value?.toString() || '';
                  });

                  XLSX.utils.sheet_add_aoa(ws, [transactionRow], {
                     origin: XLSX.utils.encode_cell({ r: rowIndex, c: 0 }),
                  });

                  // Style transaction row
                  transactionRow.forEach((_, colIndex) => {
                     ws[XLSX.utils.encode_cell({ r: rowIndex, c: colIndex })].s = {
                        font: { color: { rgb: '000000' } },
                        fill: { fgColor: { rgb: rowIndex % 2 === 0 ? 'FFFFFF' : 'F5F5F5' } },
                        alignment: {
                           horizontal: [
                              'netAmount',
                              'basicAmount',
                              'taxableAmount',
                              'gstAmount',
                              'qty1',
                              'qty2',
                              'qty3',
                           ].includes(columnIds[colIndex])
                              ? 'right'
                              : 'left',
                        },
                     };
                  });
                  rowIndex++;
               });
            });

            // Add totals row for this date
            const totalsRow = columnIds.map((colId, colIndex) => {
               if (colIndex === 0) return 'Total';
               if (['qty1', 'qty2', 'qty3'].includes(colId)) {
                  const total =
                     dateItem.data?.reduce((sum, item) => {
                        return sum + item.data?.reduce((itemSum, t) => itemSum + (Number(t[colId]) || 0), 0);
                     }, 0) || 0;
                  return formatNumber(total);
               }
               if (['netAmount', 'basicAmount', 'taxableAmount', 'gstAmount'].includes(colId)) {
                  const total =
                     dateItem.data?.reduce((sum, item) => {
                        return sum + item.data?.reduce((itemSum, t) => itemSum + (Number(t[colId]) || 0), 0);
                     }, 0) || 0;
                  return formatCurrency(total);
               }
               return '';
            });

            XLSX.utils.sheet_add_aoa(ws, [totalsRow], { origin: XLSX.utils.encode_cell({ r: rowIndex, c: 0 }) });

            // Style totals row
            totalsRow.forEach((_, colIndex) => {
               ws[XLSX.utils.encode_cell({ r: rowIndex, c: colIndex })].s = {
                  font: { bold: true, color: { rgb: '000000' } },
                  fill: { fgColor: { rgb: 'E6E6E6' } },
                  alignment: {
                     horizontal: [
                        'netAmount',
                        'basicAmount',
                        'taxableAmount',
                        'gstAmount',
                        'qty1',
                        'qty2',
                        'qty3',
                     ].includes(columnIds[colIndex])
                        ? 'right'
                        : 'left',
                  },
               };
            });
            rowIndex += 2;
         });
      }

      // Set column widths
      ws['!cols'] = visibleColumns.map((col) => {
         if (col.id === 'accountName' || col.id === 'itemName') return { wch: 40 };
         if (['netAmount', 'basicAmount', 'taxableAmount', 'gstAmount'].includes(col.id)) return { wch: 20 };
         if (['qty1', 'qty2', 'qty3'].includes(col.id)) return { wch: 15 };
         return { wch: 25 };
      });

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Sales Summary');

      // Generate file name
      const dateRangeStr =
         dateRange.fromDate && dateRange.toDate
            ? `${new Date(dateRange.fromDate).toLocaleDateString('en-IN').replace(/\//g, '-')}_to_${new Date(
               dateRange.toDate,
            )
               .toLocaleDateString('en-IN')
               .replace(/\//g, '-')}`
            : 'All_Dates';

      // Save the Excel file
      XLSX.writeFile(wb, `Sales_Summary_${dateRangeStr}.xlsx`);
   }, [salesReport, dateRange, reportType, columnVisibility, expandedItems]);

   const handleDownloadPDF = useCallback(() => {
      if (!salesReport || salesReport.length === 0) return;

      const doc = new jsPDF({
         orientation: 'landscape',
         unit: 'mm',
      });

      // Add title and date range
      const title = 'Sales Summary Report';
      const dateRange =
         dateRange.fromDate && dateRange.toDate
            ? `From ${new Date(dateRange.fromDate).toLocaleDateString('en-IN')} to ${new Date(
               dateRange.toDate,
            ).toLocaleDateString('en-IN')}`
            : 'All Dates';

      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(title, 14, 10);
      doc.setFontSize(10);
      doc.text(dateRange, 14, 16);

      // Helper functions
      const formatNumber = (value) => {
         const num = Number(value);
         return isNaN(num) ? '0' : num.toLocaleString('en-IN');
      };

      const formatCurrency = (value) => {
         const num = Number(value || 0);
         return `${num.toLocaleString('en-IN')}`;
      };

      const formatDate = (dateString) => {
         if (!dateString) return '';
         const date = new Date(dateString);
         return date.toLocaleDateString('en-IN');
      };

      // Start Y position for tables
      let startY = 25;

      if (reportType === REPORT_TYPE.party) {
         // Party-wise report format
         salesReport.forEach((companyData, companyIndex) => {
            // Check if we need a new page
            if (startY > doc.internal.pageSize.height - 60) {
               doc.addPage();
               startY = 20;
            }

            // Calculate company totals
            const companyTotals = companyData.data?.reduce(
               (acc, account) => {
                  acc.invoices += account.data?.length || 0;
                  acc.qty1 += Number(account.totalQty1) || 0;
                  acc.qty2 += Number(account.totalQty2) || 0;
                  acc.qty3 += Number(account.totalQty3) || 0;
                  acc.netAmount += Number(account.totalNetAmt) || 0;
                  return acc;
               },
               { invoices: 0, qty1: 0, qty2: 0, qty3: 0, netAmount: 0 },
            );

            // Company Header
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text(companyData.companyName || 'Unnamed Company', 14, startY);
            startY += 7;

            // Company Summary
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.text(`Invoices: ${companyTotals.invoices}`, 14, startY);
            doc.text(`Qty 1: ${formatNumber(companyTotals.qty1)}`, 50, startY);
            doc.text(`Qty 2: ${formatNumber(companyTotals.qty2)}`, 80, startY);
            doc.text(`Qty 3: ${formatNumber(companyTotals.qty3)}`, 110, startY);
            doc.text(`Net Amount: ${formatCurrency(companyTotals.netAmount)}`, 140, startY);
            startY += 8;

            // Account Summary Table
            if (companyData.data?.length) {
               const accountHeaders = ['Party Name', 'Invoices', 'Qty 1', 'Qty 2', 'Qty 3', 'Net Amount'];

               const accountRows = companyData.data.map((account) => [
                  account.accountName || 'Unnamed Account',
                  (account.data?.length || 0).toString(),
                  formatNumber(account.totalQty1),
                  formatNumber(account.totalQty2),
                  formatNumber(account.totalQty3),
                  formatCurrency(account.totalNetAmt),
               ]);

               autoTable(doc, {
                  startY,
                  head: [accountHeaders],
                  body: accountRows,
                  headStyles: {
                     fillColor: [41, 128, 185],
                     textColor: [255, 255, 255],
                     fontStyle: 'bold',
                     fontSize: 9,
                  },
                  bodyStyles: {
                     fontSize: 8,
                     textColor: [0, 0, 0],
                  },
                  alternateRowStyles: {
                     fillColor: [245, 245, 245],
                  },
                  columnStyles: {
                     0: { cellWidth: 60 },
                     1: { halign: 'right', cellWidth: 20 },
                     2: { halign: 'right', cellWidth: 20 },
                     3: { halign: 'right', cellWidth: 20 },
                     4: { halign: 'right', cellWidth: 20 },
                     5: { halign: 'right', cellWidth: 30 },
                  },
                  margin: { left: 14 },
               });

               startY = doc.lastAutoTable.finalY + 5;
            }

            // Add spacing between companies
            if (companyIndex < salesReport.length - 1) {
               startY += 10;
            }
         });
      } else {
         // Date-wise report format
         const visibleColumns = columnsReportsSalesDetail.filter((col) => columnVisibility[col.id] !== false);
         const columnIds = visibleColumns.map((col) => col.id);

         salesReport.forEach((dateItem, dateIndex) => {
            // Check if we need a new page
            if (startY > doc.internal.pageSize.height - 60) {
               doc.addPage();
               startY = 20;
            }

            // Date/Company Header
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text(dateItem.companyName || 'Unnamed Company', 14, startY);
            startY += 7;

            // Prepare transaction data
            const transactions = dateItem.data?.flatMap((item) => item.data || []) || [];

            if (transactions.length > 0) {
               const headers = visibleColumns.map((col) => col.header);

               const transactionRows = transactions.map((transaction) => {
                  return columnIds.map((colId) => {
                     if (['netAmount', 'basicAmount', 'taxableAmount', 'gstAmount'].includes(colId)) {
                        return formatCurrency(transaction[colId]);
                     } else if (colId === 'date') {
                        return formatDate(transaction[colId]);
                     } else if (colId === 'itemName' && transaction.saleLineItems) {
                        return transaction.saleLineItems.map((item) => item.itemName).join(', ');
                     }
                     return transaction[colId]?.toString() || '';
                  });
               });

               autoTable(doc, {
                  startY,
                  head: [headers],
                  body: transactionRows,
                  headStyles: {
                     fillColor: [41, 128, 185],
                     textColor: [255, 255, 255],
                     fontStyle: 'bold',
                     fontSize: 9,
                  },
                  bodyStyles: {
                     fontSize: 8,
                     textColor: [0, 0, 0],
                  },
                  alternateRowStyles: {
                     fillColor: [245, 245, 245],
                  },
                  columnStyles: columnIds.reduce((styles, colId, idx) => {
                     if (
                        ['netAmount', 'basicAmount', 'taxableAmount', 'gstAmount', 'qty1', 'qty2', 'qty3'].includes(
                           colId,
                        )
                     ) {
                        styles[idx] = { halign: 'right' };
                     }
                     return styles;
                  }, {}),
                  margin: { left: 14 },
               });

               startY = doc.lastAutoTable.finalY + 5;

               // Add totals row
               const totalsRow = columnIds.map((colId, idx) => {
                  if (idx === 0) return 'Total';
                  if (['qty1', 'qty2', 'qty3'].includes(colId)) {
                     const total = transactions.reduce((sum, t) => sum + (Number(t[colId]) || 0), 0);
                     return formatNumber(total);
                  }
                  if (['netAmount', 'basicAmount', 'taxableAmount', 'gstAmount'].includes(colId)) {
                     const total = transactions.reduce((sum, t) => sum + (Number(t[colId]) || 0), 0);
                     return formatCurrency(total);
                  }
                  return '';
               });

               autoTable(doc, {
                  startY,
                  body: [totalsRow],
                  styles: {
                     fontStyle: 'bold',
                     fontSize: 9,
                     fillColor: [230, 230, 230],
                  },
                  columnStyles: columnIds.reduce((styles, colId, idx) => {
                     if (
                        ['netAmount', 'basicAmount', 'taxableAmount', 'gstAmount', 'qty1', 'qty2', 'qty3'].includes(
                           colId,
                        )
                     ) {
                        styles[idx] = { halign: 'right' };
                     }
                     return styles;
                  }, {}),
                  margin: { left: 14 },
               });

               startY = doc.lastAutoTable.finalY + 5;
            }

            // Add spacing between date items
            if (dateIndex < salesReport.length - 1) {
               startY += 10;
            }
         });
      }

      // Add footer to all pages
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
         doc.setPage(i);
         doc.setFontSize(10);
         doc.setFont('helvetica', 'normal');
         doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 20, doc.internal.pageSize.height - 10, {
            align: 'right',
         });
         doc.text(`Generated on: ${new Date().toLocaleString('en-IN')}`, 14, doc.internal.pageSize.height - 10);
      }

      // Save the PDF
      const dateRangeStr =
         dateRange.fromDate && dateRange.toDate
            ? `${new Date(dateRange.fromDate).toLocaleDateString('en-IN').replace(/\//g, '-')}_to_${new Date(
               dateRange.toDate,
            )
               .toLocaleDateString('en-IN')
               .replace(/\//g, '-')}`
            : 'All_Dates';

      doc.save(`Sales_Summary_${dateRangeStr}.pdf`);
   }, [salesReport, dateRange, reportType, columnVisibility, expandedItems]);

   const formatCurrencyWithSmallDecimals = (value) => {
      if (value == null || value === '') return '₹0';
      const numValue = Number(value);
      if (isNaN(numValue)) return '₹0';

      const formatted = numValue.toLocaleString('en-IN', {
         minimumFractionDigits: 2,
         maximumFractionDigits: 2,
      });

      const parts = formatted.toString().split('.');

      if (parts.length === 2) {
         return (
            <>
               ₹{parts[0]}.<span style={{ fontSize: '0.7em' }}>{parts[1]}</span>
            </>
         );
      }

      return `₹${formatted}`;
   };

   const renderTableCell = ({ value, isNumeric = false, isDate = false, isCurrency = false, isBoolean = false }) => {
      let displayValue = value;

      if (isDate && value) {
         displayValue = new Date(value).toLocaleDateString();
      } else if (isCurrency && value !== undefined && value !== null) {
         // Use the formatCurrencyWithSmallDecimals function instead
         displayValue = formatCurrencyWithSmallDecimals(value);
      } else if (isBoolean) {
         displayValue = value ? 'Yes' : 'No';
      } else if (isNumeric && value !== undefined && value !== null) {
         // For other numeric values (quantities, etc.)
         displayValue = Number(value).toLocaleString();
      }

      return (
         <Td
            p="2px"
            py="3px"
            fontSize="13px"
            whiteSpace="nowrap"
            textOverflow="ellipsis"
            maxW="150px"
            overflow="hidden"
            borderColor="gray.200"
            isNumeric={isNumeric}
            minW={{ sm: '150px', md: '200px', lg: 'auto' }}
            // Add text alignment based on type
            textAlign={isNumeric || isCurrency ? 'right' : 'left'}>
            {displayValue}
         </Td>
      );
   };

   const deletePurchaseReturn = () => {
      deleteRecord(viewDetail?.id, deleteSaleInvoice, dispatch, user?.loginhistory?.companyId, userGlobalSettingsData);
      setIsOpenPurchaseReturnSingleView(false);
   };

   const handleSalesDetailsClose = () => {
      setIsOpenPurchaseReturnSingleView();
   };

   const renderDataRow = (transaction, rowIndex, isPartyReport = false) => {
      const isOddRow = rowIndex % 2 !== 0;

      const handledata = async (data) => {
         const storedCompanyId = localStorage.getItem('companyId');
         const companyId = parseInt(storedCompanyId);
         setIsModalLoading(true); // Set loading state at the start

         try {
            if (data.companyId === companyId && (admin || rights?.saleinvoice?.view)) {
               await dispatch(
                  getSingleSalesReturnWithPrevAndNext({
                     id: data.id,
                     page: 1,
                     limit: 1,
                     sortBy: 'id',
                     sortOrder: 'desc',
                  }),
               );
               setIsOpenPurchaseReturnSingleView(true);
            }
            setTimeout(() => {
               setIsModalLoading(false);
            }, 100);
         } catch (error) {
            console.error('Error loading single view data:', error);
            setIsModalLoading(false); // Clear loading state on error
         }
      };

      return (
         <Tr
            key={`row-${transaction.id || rowIndex}`}
            color={textColor}
            cursor={'pointer'}
            onClick={() => handledata(transaction)}
            bg={isOddRow ? (isDark ? 'rgba(30, 41, 59, 0.4)' : 'rgba(241, 245, 249, 0.8)') : 'transparent'}
            _hover={{
               bg: isDark ? 'rgba(51, 65, 85, 0.6)' : 'rgba(226, 232, 240, 0.9)',
               transform: 'translateX(2px)',
               transition: 'all 0.2s ease',
            }}
            transition="all 0.2s ease"
            pl={isPartyReport ? 4 : 0}>
            {table
               .getAllColumns()
               .filter((column) => column.getIsVisible())
               .map((column) => {
                  const columnId = column.id;
                  const value = transaction[columnId];

                  if (
                     columnId === 'itemName' &&
                     transaction.saleLineItems &&
                     Array.isArray(transaction.saleLineItems)
                  ) {
                     return (
                        <Td
                           borderColor={borderColor}
                           key={columnId}
                           p="2px"
                           py="3px"
                           fontSize="13px"
                           whiteSpace="nowrap"
                           textOverflow="ellipsis"
                           fontWeight={600}
                           maxW="150px"
                           overflow="hidden"
                           minW={{ sm: '150px', md: '200px', lg: 'auto' }}>
                           {transaction.saleLineItems.map((item) => item.itemName).join(', ')}
                        </Td>
                     );
                  }

                  const isNumeric = [
                     'netAmount',
                     'basicAmount',
                     'taxableAmount',
                     'gstAmount',
                     'qty1',
                     'qty2',
                     'qty3',
                     'dueDays',
                     'returnadjamount',
                     'otheradjamount',
                     'balanceamount',
                  ].includes(columnId);

                  const isDate = columnId.includes('Date');
                  const isCurrency = ['netAmount', 'basicAmount', 'taxableAmount', 'gstAmount'].includes(columnId);

                  return renderTableCell({
                     value,
                     isNumeric,
                     isDate,
                     isCurrency,
                     isBoolean: typeof value === 'boolean',
                  });
               })}
         </Tr>
      );
   };

   const renderPartyReportContent = () => {
      return salesReport.map((companyData, companyIndex) => (
         <React.Fragment key={`company-${companyData.companyId || companyIndex}`}>
            {/* Company Level - Premium Header */}
            <Tr
               bg={
                  isDark
                     ? 'linear-gradient(135deg, #1e293b 0%, #334155 100%)'
                     : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
               }
               borderLeft={isDark ? '4px solid #3b82f6' : '4px solid #2563eb'}
               borderBottom={isDark ? '2px solid #1e293b' : '2px solid #cbd5e1'}
               boxShadow={isDark ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)'}
               cursor="pointer"
               _hover={{
                  bg: isDark
                     ? 'linear-gradient(135deg, #334155 0%, #475569 100%)'
                     : 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)',
                  borderLeftColor: isDark ? '#60a5fa' : '#3b82f6',
                  transform: 'translateX(4px)',
                  transition: 'all 0.3s ease',
               }}
               transition="all 0.3s ease"
               onClick={() => toggleExpand(`company-${companyData.companyId}`)}>
               <Td
                  p="12px"
                  py="16px"
                  fontSize="10px"
                  whiteSpace="nowrap"
                  color={textColor}
                  textOverflow="ellipsis"
                  maxW="150px"
                  overflow="hidden">
                  <Flex align="center" gap={2}>
                     <Box
                        w="8px"
                        h="8px"
                        borderRadius="full"
                        bg={isDark ? '#3b82f6' : '#2563eb'}
                        boxShadow={isDark ? '0 0 8px #3b82f6' : '0 0 8px #2563eb'}
                     />
                     <Text fontWeight={'800'} fontSize={'lg'} letterSpacing="wide">
                        {companyData.companyName}
                     </Text>
                  </Flex>
               </Td>
               <Td
                  p="12px"
                  py="16px"
                  fontSize="13px"
                  whiteSpace="nowrap"
                  color={isDark ? '#94a3b8' : '#475569'}
                  textOverflow="ellipsis"
                  maxW="150px"
                  overflow="hidden"
                  fontWeight="700"
                  isNumeric>
                  <Text as="span" color={isDark ? '#60a5fa' : '#3b82f6'} fontWeight="800">
                     {companyData.data.reduce((sum, account) => sum + account.data.length, 0)}
                  </Text>{' '}
                  Invoices
               </Td>
               <Td
                  p="12px"
                  py="16px"
                  fontSize="13px"
                  whiteSpace="nowrap"
                  color={isDark ? '#94a3b8' : '#475569'}
                  textOverflow="ellipsis"
                  maxW="150px"
                  overflow="hidden"
                  fontWeight="700"
                  isNumeric>
                  <Text as="span" color={isDark ? '#34d399' : '#10b981'} fontWeight="800">
                     {companyData.data.reduce((sum, account) => sum + (Number(account.totalQty1) || 0), 0)}
                  </Text>
               </Td>
               <Td
                  p="12px"
                  py="16px"
                  fontSize="13px"
                  whiteSpace="nowrap"
                  color={isDark ? '#94a3b8' : '#475569'}
                  textOverflow="ellipsis"
                  maxW="150px"
                  overflow="hidden"
                  fontWeight="700"
                  isNumeric>
                  <Text as="span" color={isDark ? '#fbbf24' : '#f59e0b'} fontWeight="800">
                     {companyData.data.reduce((sum, account) => sum + (Number(account.totalQty2) || 0), 0)}
                  </Text>
               </Td>
               <Td
                  p="12px"
                  py="16px"
                  fontSize="13px"
                  whiteSpace="nowrap"
                  color={isDark ? '#94a3b8' : '#475569'}
                  textOverflow="ellipsis"
                  maxW="150px"
                  overflow="hidden"
                  fontWeight="700"
                  isNumeric>
                  <Text as="span" color={isDark ? '#a78bfa' : '#8b5cf6'} fontWeight="800">
                     {companyData.data.reduce((sum, account) => sum + (Number(account.totalQty3) || 0), 0)}
                  </Text>
               </Td>
               <Td
                  p="12px"
                  py="16px"
                  fontSize="14px"
                  whiteSpace="nowrap"
                  color={textColor}
                  textOverflow="ellipsis"
                  maxW="150px"
                  overflow="hidden"
                  fontWeight="800"
                  isNumeric>
                  <Text as="span" color={isDark ? '#4ade80' : '#22c55e'}>
                     ₹
                     {companyData.data
                        .reduce((sum, account) => sum + (Number(account.totalNetAmt) || 0), 0)
                        .toLocaleString()}
                  </Text>
               </Td>
            </Tr>

            {/* Account Level - Nested with Beautiful Borders */}
            {expandedItems[`company-${companyData.companyId}`] &&
               companyData.data &&
               Array.isArray(companyData.data) && (
                  <>
                     <Tr
                        bg={isDark ? 'rgba(30, 41, 59, 0.6)' : 'rgba(248, 250, 252, 0.9)'}
                        borderTop={isDark ? '2px solid #475569' : '2px solid #cbd5e1'}
                        color={textColor}>
                        <Th
                           borderColor={isDark ? '#334155' : '#e2e8f0'}
                           p="8px"
                           py="10px"
                           fontSize="11px"
                           fontWeight="700"
                           width="40%"
                           color={isDark ? '#cbd5e1' : '#475569'}
                           textTransform="uppercase"
                           letterSpacing="wider">
                           Party Name
                        </Th>
                        <Th
                           borderColor={isDark ? '#334155' : '#e2e8f0'}
                           p="8px"
                           py="10px"
                           fontSize="11px"
                           fontWeight="700"
                           width="20%"
                           color={isDark ? '#cbd5e1' : '#475569'}
                           textTransform="uppercase"
                           letterSpacing="wider"
                           isNumeric>
                           Invoices
                        </Th>
                        <Th
                           borderColor={isDark ? '#334155' : '#e2e8f0'}
                           p="8px"
                           py="10px"
                           fontSize="11px"
                           fontWeight="700"
                           width="10%"
                           color={isDark ? '#cbd5e1' : '#475569'}
                           textTransform="uppercase"
                           letterSpacing="wider"
                           isNumeric>
                           Qty 1
                        </Th>
                        <Th
                           borderColor={isDark ? '#334155' : '#e2e8f0'}
                           p="8px"
                           py="10px"
                           fontSize="11px"
                           fontWeight="700"
                           width="10%"
                           color={isDark ? '#cbd5e1' : '#475569'}
                           textTransform="uppercase"
                           letterSpacing="wider"
                           isNumeric>
                           Qty 2
                        </Th>
                        <Th
                           borderColor={isDark ? '#334155' : '#e2e8f0'}
                           p="8px"
                           py="10px"
                           fontSize="11px"
                           fontWeight="700"
                           width="10%"
                           color={isDark ? '#cbd5e1' : '#475569'}
                           textTransform="uppercase"
                           letterSpacing="wider"
                           isNumeric>
                           Qty 3
                        </Th>
                        <Th
                           borderColor={isDark ? '#334155' : '#e2e8f0'}
                           p="8px"
                           py="10px"
                           fontSize="11px"
                           fontWeight="700"
                           width="20%"
                           color={isDark ? '#cbd5e1' : '#475569'}
                           textTransform="uppercase"
                           letterSpacing="wider"
                           isNumeric>
                           Net Amount
                        </Th>
                     </Tr>

                     {companyData.data.map((accountData, accountIndex) => (
                        <Tr
                           key={`party-${accountData.accountId || accountIndex}`}
                           bg={isDark ? 'rgba(51, 65, 85, 0.3)' : 'rgba(255, 255, 255, 0.8)'}
                           borderLeft={isDark ? '3px solid #64748b' : '3px solid #94a3b8'}
                           borderBottom={isDark ? '1px solid #334155' : '1px solid #e2e8f0'}
                           cursor="pointer"
                           _hover={{
                              bg: isDark ? 'rgba(71, 85, 105, 0.5)' : 'rgba(241, 245, 249, 1)',
                              borderLeftColor: isDark ? '#94a3b8' : '#64748b',
                              transform: 'translateX(8px)',
                              transition: 'all 0.2s ease',
                           }}
                           transition="all 0.2s ease"
                           onClick={() => toggleExpand(`account-${accountData.accountId}`)}>
                           <Td
                              p="8px"
                              py="10px"
                              fontSize="12px"
                              whiteSpace="nowrap"
                              textOverflow="ellipsis"
                              maxW="150px"
                              overflow="hidden"
                              fontWeight="600"
                              color={textColor}>
                              <Flex align="center" gap={2}>
                                 <Box w="6px" h="6px" borderRadius="full" bg={isDark ? '#64748b' : '#94a3b8'} />
                                 {accountData.accountName}
                              </Flex>
                           </Td>
                           <Td
                              p="8px"
                              py="10px"
                              fontSize="12px"
                              whiteSpace="nowrap"
                              textOverflow="ellipsis"
                              maxW="150px"
                              overflow="hidden"
                              fontWeight="600"
                              color={isDark ? '#94a3b8' : '#64748b'}
                              isNumeric>
                              {accountData.data.length}
                           </Td>
                           <Td
                              p="8px"
                              py="10px"
                              fontSize="12px"
                              whiteSpace="nowrap"
                              textOverflow="ellipsis"
                              maxW="150px"
                              overflow="hidden"
                              fontWeight="600"
                              color={isDark ? '#94a3b8' : '#64748b'}
                              isNumeric>
                              {accountData.totalQty1}
                           </Td>
                           <Td
                              p="8px"
                              py="10px"
                              fontSize="12px"
                              whiteSpace="nowrap"
                              textOverflow="ellipsis"
                              maxW="150px"
                              overflow="hidden"
                              fontWeight="600"
                              color={isDark ? '#94a3b8' : '#64748b'}
                              isNumeric>
                              {accountData.totalQty2}
                           </Td>
                           <Td
                              p="8px"
                              py="10px"
                              fontSize="12px"
                              whiteSpace="nowrap"
                              textOverflow="ellipsis"
                              maxW="150px"
                              overflow="hidden"
                              fontWeight="600"
                              color={isDark ? '#94a3b8' : '#64748b'}
                              isNumeric>
                              {accountData.totalQty3}
                           </Td>
                           <Td
                              p="8px"
                              py="10px"
                              fontSize="12px"
                              whiteSpace="nowrap"
                              textOverflow="ellipsis"
                              maxW="150px"
                              overflow="hidden"
                              fontWeight="700"
                              color={isDark ? '#4ade80' : '#22c55e'}
                              isNumeric>
                              ₹{Number(accountData.totalNetAmt).toLocaleString()}
                           </Td>
                        </Tr>
                     ))}
                  </>
               )}
         </React.Fragment>
      ));
   };

   const renderDateReportContent = () => {
      return salesReport.map((dateItem, index) => {
         const itemData = dateItem.data?.flatMap((item) => item.data || []) || [];

         return (
            <React.Fragment key={`date-${dateItem.date || index}`}>
               <Tr
                  bg={
                     isDark
                        ? 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)'
                        : 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)'
                  }
                  borderLeft={isDark ? '4px solid #3b82f6' : '4px solid #2563eb'}
                  borderBottom={isDark ? '2px solid #1e293b' : '2px solid #93c5fd'}
                  boxShadow={isDark ? '0 4px 12px rgba(59, 130, 246, 0.3)' : '0 4px 12px rgba(37, 99, 235, 0.15)'}
                  fontWeight="bold"
                  color={textColor}
                  pl={4}>
                  <Td
                     p="12px"
                     py="16px"
                     fontSize="10px"
                     whiteSpace="nowrap"
                     textOverflow="ellipsis"
                     maxW="150px"
                     color={textColor}
                     overflow="hidden"
                     colSpan={15}>
                     <Flex justifyContent="space-between" alignItems="center">
                        <Flex align="center" gap={3}>
                           <Box
                              w="10px"
                              h="10px"
                              borderRadius="full"
                              bg={isDark ? '#60a5fa' : '#3b82f6'}
                              boxShadow={isDark ? '0 0 10px #60a5fa' : '0 0 10px #3b82f6'}
                           />
                           <Text fontWeight="800" fontSize="lg" letterSpacing="wide">
                              {dateItem.companyName}
                           </Text>
                        </Flex>
                     </Flex>
                  </Td>
               </Tr>
               {table.getHeaderGroups().map((headerGroup) => (
                  <Tr
                     key={headerGroup.id}
                     bg={isDark ? 'rgba(30, 41, 59, 0.8)' : 'rgba(248, 250, 252, 0.95)'}
                     borderBottom={isDark ? '2px solid #475569' : '2px solid #cbd5e1'}
                     position="sticky"
                     top="-1"
                     zIndex="1">
                     {headerGroup.headers.map((header) => {
                        if (!header.column.getIsVisible()) return null;
                        const columnId = header.column.id;
                        const isNumericColumn = [
                           'netAmount',
                           'dueDays',
                           'returnadjamount',
                           'otheradjamount',
                           'balanceamount',
                           'recDate',
                           'recBook',
                        ].includes(columnId);

                        return (
                           <Th
                              key={header.id}
                              colSpan={header.colSpan}
                              borderColor={isDark ? '#334155' : '#e2e8f0'}
                              cursor="pointer"
                              p="8px"
                              py="10px"
                              fontSize="11px"
                              fontWeight="700"
                              color={isDark ? '#cbd5e1' : '#475569'}
                              textTransform="uppercase"
                              letterSpacing="wider"
                              whiteSpace="nowrap"
                              _hover={{
                                 bg: isDark ? 'rgba(51, 65, 85, 0.8)' : 'rgba(226, 232, 240, 0.8)',
                              }}
                              onClick={header.column.getToggleSortingHandler()}>
                              <Flex
                                 justifyContent={isNumericColumn ? 'flex-end' : 'space-between'}
                                 align="center"
                                 fontSize={{ sm: '10px', lg: '12px' }}
                                 color={isDark ? '#94a3b8' : '#64748b'}>
                                 {flexRender(header.column.columnDef.header, header.getContext())}
                                 {{
                                    asc: '↑',
                                    desc: '↓',
                                 }[header.column.getIsSorted()] ?? null}
                              </Flex>
                           </Th>
                        );
                     })}
                  </Tr>
               ))}
               {dateItem.data?.map((item, itemIndex) => (
                  <React.Fragment key={`item-${itemIndex}`}>
                     {Array.isArray(item.data) &&
                        item.data.map((transaction, rowIndex) => renderDataRow(transaction, rowIndex))}
                  </React.Fragment>
               ))}

               {itemData.length > 0 && (
                  <Tr
                     bg={
                        isDark
                           ? 'linear-gradient(135deg, #059669 0%, #10b981 100%)'
                           : 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)'
                     }
                     borderTop={isDark ? '3px solid #34d399' : '3px solid #10b981'}
                     borderBottom={isDark ? '3px solid #34d399' : '3px solid #10b981'}
                     boxShadow={isDark ? '0 4px 12px rgba(16, 185, 129, 0.3)' : '0 4px 12px rgba(16, 185, 129, 0.2)'}
                     fontWeight="bold"
                     color={textColor}
                     pl={4}>
                     {table
                        .getAllColumns()
                        .filter((column) => column.getIsVisible())
                        .map((column, colIndex, visibleCols) => {
                           if (column.id === visibleCols[0].id) {
                              return (
                                 <Td
                                    key={`grand-total-${column.id}`}
                                    p="10px"
                                    py="14px"
                                    fontSize="16px"
                                    fontWeight={800}
                                    color={isDark ? '#fff' : '#065f46'}
                                    letterSpacing="wide"
                                    borderColor="gray.200"
                                    minW={{ sm: '150px', md: '200px', lg: 'auto' }}>
                                    GRAND TOTAL
                                 </Td>
                              );
                           }

                           if (column.id === 'qty' || column.id.toLowerCase().includes('qty')) {
                              const grandTotalQty = itemData.reduce((sum, t) => {
                                 const val = Number(t[column.id]);
                                 return sum + (isNaN(val) ? 0 : val);
                              }, 0);

                              return (
                                 <Td
                                    key={`grand-total-${column.id}`}
                                    p="10px"
                                    py="14px"
                                    fontSize="15px"
                                    fontWeight={800}
                                    color={isDark ? '#fff' : '#065f46'}
                                    borderColor="gray.200"
                                    minW={{ sm: '150px', md: '200px', lg: 'auto' }}>
                                    {grandTotalQty}
                                 </Td>
                              );
                           }

                           if (column.id === 'netAmount') {
                              const total = itemData.reduce((sum, t) => sum + (Number(t.netAmount) || 0), 0);
                              return renderTableCell({
                                 value: total,
                                 isNumeric: true,
                                 isCurrency: true,
                              });
                           }

                           if (['basicAmount', 'taxableAmount', 'gstAmount'].includes(column.id)) {
                              const total = itemData.reduce((sum, t) => sum + (Number(t[column.id]) || 0), 0);
                              return renderTableCell({
                                 value: total,
                                 isNumeric: true,
                                 isCurrency: true,
                              });
                           }

                           return <Td key={`grand-total-${column.id}`}></Td>;
                        })}
                  </Tr>
               )}
            </React.Fragment>
         );
      });
   };

   const renderReportContent = () => {
      if (isLoading) {
         return (
            <Tr>
               <Td colSpan={table.getVisibleFlatColumns().length} textAlign="center" py="100px">
                  <Loader />
               </Td>
            </Tr>
         );
      }

      if (!salesReport || salesReport.length === 0) {
         return (
            <Tr justifyContent="center" alignItems="center" width="100%" height="100%">
               <Td
                  p="2px"
                  py="3px"
                  fontSize="10px"
                  whiteSpace="nowrap"
                  textOverflow="ellipsis"
                  maxW="150px"
                  overflow="hidden"
                  colSpan={table.getVisibleFlatColumns().length}
                  justifyContent="center"
                  alignItems="center"
                  width="100%">
                  <Grid justifyContent="center" alignItems="center" gap={9}>
                     <img src={nodata} alt="No Data Found" width="200px" height="200px" />
                     <GridItem justifySelf="center">
                        <label>No sales data found</label>
                     </GridItem>
                  </Grid>
               </Td>
            </Tr>
         );
      }

      return reportType === REPORT_TYPE.party ? renderPartyReportContent() : renderDateReportContent();
   };

   const ColumnVisibilityModal = () => (
      <Modal isOpen={isColumnSelectorOpen} onClose={() => setIsColumnSelectorOpen(false)} size="md">
         <ModalOverlay />
         <ModalContent>
            <ModalHeader>Show/Hide Columns</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
               {columnsReportsSalesDetail.map((column) => (
                  <Flex key={column.id} mb={2} alignItems="center">
                     <Checkbox
                        isChecked={!!columnVisibility[column.id]}
                        onChange={(e) => {
                           table.setColumnVisibility((prev) => ({
                              ...prev,
                              [column.id]: e.target.checked,
                           }));
                        }}
                        mr={2}
                     />
                     <Text>{column.header}</Text>
                  </Flex>
               ))}
            </ModalBody>
            <ModalFooter>
               <Button colorScheme="blue" onClick={() => setIsColumnSelectorOpen(false)}>
                  Close
               </Button>
            </ModalFooter>
         </ModalContent>
      </Modal>
   );

   return (
      <>
         {/* Title Outside Card */}
         <Flex pr="25px" mb="8px" justifyContent="space-between" align="center" mt="50px" overflow={'hidden'}>
            <Flex>
               <Image
                  src={back}
                  alt="Go Back"
                  width="15px"
                  onClick={() => window.history.back()}
                  style={{ cursor: 'pointer' }}
               />
               <Text color={textColor} fontSize="15px" fontWeight="700" lineHeight="100%" ml={5}>
                  Sale Return Summary
               </Text>
            </Flex>
         </Flex>

         <Box mt="15px" bg={backgroundColor} px={4} py={0} borderRadius="7px">
            <Flex alignItems="center" gap={3} justifyContent={'end'}>
               <Box my={2} width="300px" fontSize={'13px'} fontWeight={500} display={{ base: 'none', xl: 'block' }}>
                  <GridItem className="seperator-width" mt={5}>
                     {renderDynamicDropdown(
                        'company',
                        'Company',
                        lastcompanyOptions,
                        { value: companyFilter, onChange: handleCompanyChange },
                        {},
                        textColor,
                        false,
                        'Select company',
                        true,
                        undefined,
                        true,
                     )}
                  </GridItem>
               </Box>

               <Box flex="1" display={{ base: 'none', xl: 'flex' }}>
                  <Flex alignItems={'center'} mt={3}>
                     <PeriodDatePicker
                        value={[new Date(dateRange.fromDate), new Date(dateRange.toDate)]}
                        onChange={handleDateRangeChange}
                        defaultPeriod="last7Days"
                        style={{ width: 250, marginRight: 20, borderRadius: 5, height: 35 }}
                     />
                  </Flex>

                  <Box my={2} zIndex={3} fontSize={'13px'} fontWeight={500}>
                     <label>Report Type</label>
                     <Select
                        options={reportTypeOptions}
                        value={reportTypeOptions.find((option) => option.value === reportType)}
                        onChange={(selectedOption) => {
                           setReportType(selectedOption.value);
                           setExpandedItems({});
                        }}
                        placeholder="Select Report Type"
                        styles={{
                           control: (baseStyles, state) => ({
                              ...baseStyles,
                              minHeight: '30px',
                              height: '30px',
                              backgroundColor: controlBg,
                              borderColor: controlBorder,
                              boxShadow: state.isFocused ? focusBoxShadow : baseStyles.boxShadow,
                              '&:hover': {
                                 borderColor: controlBorderHover,
                              },
                           }),
                           valueContainer: (baseStyles) => ({
                              ...baseStyles,
                              height: '30px',
                              padding: '0 6px',
                              color: textColor,
                           }),
                           indicatorsContainer: (baseStyles) => ({
                              ...baseStyles,
                              height: '30px',
                           }),
                           singleValue: (baseStyles) => ({
                              ...baseStyles,
                              color: textColor,
                           }),
                           placeholder: (baseStyles) => ({
                              ...baseStyles,
                              color: placeholderColor,
                           }),
                           option: (baseStyles, state) => ({
                              ...baseStyles,
                              backgroundColor: state.isFocused ? optionHoverBg : optionBg,
                              color: textColor,
                              '&:hover': {
                                 backgroundColor: optionHoverBg,
                              },
                           }),
                           menu: (baseStyles) => ({
                              ...baseStyles,
                              backgroundColor: menuBg,
                              border: `1px solid ${menuBorder}`,
                           }),
                           dropdownIndicator: (baseStyles) => ({
                              ...baseStyles,
                              color: indicatorColor,
                              '&:hover': {
                                 color: indicatorHoverColor,
                              },
                           }),
                           clearIndicator: (baseStyles) => ({
                              ...baseStyles,
                              color: indicatorColor,
                              '&:hover': {
                                 color: indicatorHoverColor,
                              },
                           }),
                        }}
                     />
                  </Box>
               </Box>

               <Flex>
                  <Box flex="1" mr={3}>
                     <MotionFlex
                        ref={wrapperRef}
                        align="center"
                        bg={bg}
                        borderRadius="full"
                        cursor="pointer"
                        px={expanded ? 3 : 0}
                        w={expanded ? '250px' : '40px'}
                        h="40px"
                        transition="all 0.3s ease"
                        onClick={() => setExpanded(true)}>
                        <MotionSearchIcon
                           color={icon}
                           animate={{ rotate: expanded ? 0 : 90 }}
                           transition={{ duration: 0.3 }}
                           style={{
                              marginRight: expanded ? '8px' : '10px',
                              marginLeft: expanded ? '0' : '10px',
                           }}
                        />
                        {expanded && (
                           <Input
                              variant="unstyled"
                              placeholder="Search ..."
                              value={searchTerm}
                              onChange={handleSearchChange}
                              autoFocus
                              color={icon}
                              flex="1"
                           />
                        )}
                     </MotionFlex>
                  </Box>

                  <Button onClick={handleRefresh} _hover={{ bg: 'blue.50' }} mr={2}>
                     <LuRefreshCcw size={20} mr={2} />
                  </Button>

                  <Button cursor={'pointer'} onClick={onFilterOpen} mr={2}>
                     <IoFilterOutline size={22} />
                  </Button>

                  <Flex>
                     <Flex position="relative" mr={2}>
                        <Tooltip label="Show/Hide Columns">
                           <IconButton
                              icon={<ViewIcon />}
                              onClick={() => setIsColumnSelectorOpen(true)}
                              aria-label="Column Visibility"
                           />
                        </Tooltip>
                     </Flex>

                     <Flex mr={2}>
                        <Popover>
                           <PopoverTrigger>
                              <IconButton
                                 icon={<Image src={Download} boxSize="24px" />}
                                 aria-label="Download Options"
                              />
                           </PopoverTrigger>
                           <PopoverContent width="220px" zIndex="10" bg={backgroundColor} mr={8}>
                              <PopoverBody p="3">
                                 <Button
                                    width="100%"
                                    variant="ghost"
                                    _hover={{ bg: 'blue.100' }}
                                    onClick={handleDownloadExcel}
                                    justifyContent="space-between"
                                    mb={3}>
                                    <img src={xls} alt="Download XLS" width="30px" />
                                    Download Excel
                                 </Button>
                                 <Button
                                    width="100%"
                                    variant="ghost"
                                    _hover={{ bg: 'blue.100' }}
                                    onClick={handleDownloadPDF}
                                    justifyContent="space-between">
                                    <img src={xls} alt="Download PDF" width="30px" />
                                    Download PDF
                                 </Button>
                              </PopoverBody>
                           </PopoverContent>
                        </Popover>
                     </Flex>
                  </Flex>
               </Flex>

               <Box ml={1}>
                  <Flex display={{ base: 'block', xl: 'none' }}>
                     <Button variant="unstyled" onClick={onOpen}>
                        <Image src={DotIcon} alt="Menu" width="25px" />
                     </Button>
                  </Flex>
               </Box>

               <Drawer isOpen={isOpen} placement="bottom" onClose={onClose} finalFocusRef={fromDateInputRef}>
                  <DrawerOverlay />
                  <DrawerContent borderTopRadius="20px">
                     <DrawerCloseButton />
                     <DrawerHeader borderBottomWidth="1px">Filters</DrawerHeader>
                     <DrawerBody>
                        <GridItem className="seperator-width">
                           {renderDynamicDropdown(
                              'company',
                              'Company',
                              lastcompanyOptions,
                              { value: companyFilter, onChange: handleCompanyChange },
                              {},
                              textColor,
                              false,
                              'Select company',
                              true,
                              undefined,
                              true,
                           )}
                        </GridItem>

                        <Box my={2} fontSize={'13px'} fontWeight={500}>
                           <label>Report Type</label>
                           <Select
                              options={reportTypeOptions}
                              value={reportTypeOptions.find((option) => option.value === reportType)}
                              onChange={(selectedOption) => {
                                 setReportType(selectedOption.value);
                                 setExpandedItems({});
                              }}
                              placeholder="Select Report Type"
                           />
                        </Box>

                        <Box alignItems={'center'} mt={3}>
                           <PeriodDatePicker
                              value={[new Date(dateRange.fromDate), new Date(dateRange.toDate)]}
                              onChange={handleDateRangeChange}
                              defaultPeriod="last7Days"
                              style={{ width: 250, marginRight: 20, borderRadius: 5, height: 35 }}
                           />
                        </Box>

                        <Button colorScheme="green" width="100%" onClick={onClose} mt={4}>
                           Apply Filters
                        </Button>
                     </DrawerBody>
                  </DrawerContent>
               </Drawer>
            </Flex>
         </Box>

         <Grid overflow="auto">
            <Card mt="15px" borderRadius="7px">
               <Box
                  overflowX="auto"
                  className="custom-scrollbar"
                  position="relative"
                  minH={{ base: '60vh', md: '65vh', xl: '70vh' }}
                  maxH={{ base: '60vh', md: '65vh', xl: '70vh' }}>
                  {isFilterLoading ? (
                     <Flex justifyContent="center" alignItems="center" h="100%">
                        <Loader size="lg" color="green.500" />
                     </Flex>
                  ) : (
                     <Table variant="simple" color={textColor} mb="24px" mt="12px">
                        {/* {renderTableHeader()} */}
                        <Tbody>{renderReportContent()}</Tbody>
                     </Table>
                  )}
               </Box>
            </Card>
         </Grid>

         <ColumnVisibilityModal />

         <SalesPurchaseFilter
            type={reportType}
            isOpen={isFilterOpen}
            onClose={onFilterClose}
            companyIds={companyIds}
            fromDate={dateRange.fromDate}
            toDate={dateRange.toDate}
            entityType="salesDetail"
            onApply={() => setIsFilterLoading(true)}
         />

         {isOpenPurchaseReturnSingleView && (
            <div className="fixed inset-0 z-[999]">
               {isModalLoading ? (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
                     <Loader />
                  </div>
               ) : viewDetail ? (
                  <SalesDetails
                     isOpen={isOpenPurchaseReturnSingleView}
                     onClose={handleSalesDetailsClose}
                     singleEntityRights={saleInvoiceRight || saleReturnRight}
                     title={'Sales Return'}
                     entityType={'salereturn'}
                     singleEntity={viewDetail}
                     handlePrevPage={handlePrevPage}
                     handleNextPage={handleNextPage}
                     setIsEditFormOpen={setIsEditFormOpen}
                     setIsOpenSingleView={setIsOpenPurchaseReturnSingleView}
                     isEditFormOpen={isEditFormOpen}
                     // paginate={paginateData}
                     viewDetail={viewDetail}
                     lineItems={viewDetail?.lineitems}
                     billTerms={viewDetail?.purchasereturnbillteams}
                     onDelete={deletePurchaseReturn}
                     handleSerchClose={() => console.log('')}
                  />
               ) : (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
                     <Loader />
                  </div>
               )}
            </div>
         )}
      </>
   );
};

export default SalesReturnSummary;
